// Camera acquisition + lifecycle. Maps the messy getUserMedia error surface
// into our stable EngineErrorCode set so the UI can show the right recovery
// path, and guarantees tracks are released on stop() (no "camera stays on"
// bug, which matters doubly for the shared kiosk device).

import type { EngineErrorCode } from './types';

export interface CameraHandle {
  stream: MediaStream;
  video: HTMLVideoElement;
  stop(): void;
}

export class CameraError extends Error {
  constructor(public code: EngineErrorCode) {
    super(code);
    this.name = 'CameraError';
  }
}

function mapGetUserMediaError(err: unknown): EngineErrorCode {
  const name = err instanceof Error ? err.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'permission-denied';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'no-camera';
    case 'NotReadableError':
    case 'AbortError':
      return 'camera-in-use'; // device busy in another app/tab
    case 'TypeError':
      return 'insecure-context'; // getUserMedia undefined (http / old browser)
    default:
      return 'unknown';
  }
}

export interface StartCameraOptions {
  width: number;
  height: number;
}

export async function startCamera(
  video: HTMLVideoElement,
  opts: StartCameraOptions,
): Promise<CameraHandle> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new CameraError('insecure-context');
  }

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: 'user',
        width: { ideal: opts.width },
        height: { ideal: opts.height },
      },
    });
  } catch (err) {
    throw new CameraError(mapGetUserMediaError(err));
  }

  video.srcObject = stream;
  video.setAttribute('playsinline', 'true');
  video.muted = true;

  await new Promise<void>((resolve, reject) => {
    const onReady = () => {
      video.removeEventListener('loadedmetadata', onReady);
      resolve();
    };
    video.addEventListener('loadedmetadata', onReady);
    // Guard against a metadata event that never fires.
    setTimeout(() => {
      if (video.readyState >= 1) resolve();
      else reject(new CameraError('unknown'));
    }, 8000);
  });

  try {
    await video.play();
  } catch {
    // Autoplay can reject on some browsers; tracking still works once playing.
  }

  return {
    stream,
    video,
    stop() {
      for (const track of stream.getTracks()) track.stop();
      video.srcObject = null;
    },
  };
}
